import { Table, Flex, Tooltip, Typography, Button } from "antd";

import TaskIcon from "@/icons/TaskIcon";
import CubIcon from "@/icons/CubIcon";

import EmployeeProfile from "@/components/cards/EmployeeProfile";
import CustomLink from "@/components/ui/CustomLink";
import DownloadLink from "@/components/ui/DowloadLink";
import ExpandableText from "@/components/views/ObjectFieldValue/ExpandableText";

import routes from "@/routes";

import { getIconByType } from "@/utils";
import dayjs from "dayjs";

const iconStyle = { fontSize: 20, color: "#BBBBBB" };

const getObjectDefinitionUuid = (data) =>
  data?.definition_uuid || data?.definition?.uuid;

const getResultName = (type, data) => {
  if (type === "task") {
    return `${data?.task_number ? `№${data?.task_number} ` : ""} ${data?.name || ""}`.trim();
  }

  if (type === "file") {
    const fileName = data?.name || data?.original_name || "";
    return data?.extension ? `${fileName}.${data.extension}` : fileName;
  }

  return data?.name || "";
};

const getResultLink = ({ type, data, isAdmins }) => {
  if (type === "task") {
    return isAdmins
      ? routes.administration.taskDetail(data.uuid)
      : routes.taskDetail(data.uuid);
  }

  if (type === "object") {
    const definitionUuid = getObjectDefinitionUuid(data);

    if (!definitionUuid) return null;

    return isAdmins
      ? routes.administration.objectDetail(definitionUuid, data.uuid)
      : routes.objectDetail(definitionUuid, data.uuid);
  }

  return null;
};

const getResultPathText = ({ type, data, isAdmins }) => {
  const root = isAdmins ? "Адм. панель" : "Главная";

  if (type === "task") {
    const taskName = getResultName(type, data);
    const parts = [root, isAdmins ? "Все задачи" : "Задачи"];

    if (taskName) parts.push(taskName);

    return parts.join(" / ");
  }

  if (type === "object") {
    const definitionName = data?.definition?.name;
    const objectName = getResultName(type, data);
    const parts = [root, isAdmins ? "Все ресурсы" : "Ресурсы"];

    if (definitionName) parts.push(definitionName);
    if (objectName) parts.push(objectName);

    return parts.join(" / ");
  }

  if (type === "file") {
    const folderPath = data?.fullPath || "";

    const parts = [root, "Файлы"];

    if (folderPath) {
      const formattedPath = folderPath
        .split("/")
        .map((part) => part.trim())
        .filter(Boolean)
        .join(" / ");

      if (formattedPath) {
        parts.push(formattedPath);
      }
    }

    if (data?.name) {
      parts.push(data.name);
    }

    return parts.join(" / ");
  }

  return "";
};

const renderTypeIcon = (type, data) => {
  if (type === "file") {
    return getIconByType(
      data?.type,
      data?.extension,
      data?.element_type,
      iconStyle.fontSize,
      iconStyle.color,
    );
  }

  if (type === "object") {
    return <CubIcon style={iconStyle} />;
  }

  if (type === "task") {
    return <TaskIcon style={iconStyle} />;
  }

  return null;
};

const formatDate = (value) => {
  const date = dayjs(value);
  return date.isValid() ? date.format("DD.MM.YY") : "-";
};

const SearchResultsTable = ({
  data,
  loading,
  pagination,
  onChange,
  isAdmins = false,
  scroll = { x: "100%" },
  setFileModal,
}) => {
  const columns = [
    {
      title: "Тип",
      key: "type",
      width: 52,
      align: "center",
      render: (_, record) => (
        <Flex align="center" justify="center">
          {renderTypeIcon(record?.type, record?.data)}
        </Flex>
      ),
    },
    {
      title: "Дата создания",
      key: "created_at",
      width: 100,
      render: (_, record) => (
        <span style={{ color: "#bbb" }}>
          {formatDate(record?.data?.created_at)}
        </span>
      ),
    },
    {
      title: "Название",
      key: "name",
      ellipsis: true,
      width: 150,
      render: (_, record) => {
        const { type, data: itemData } = record || {};
        const name = getResultName(type, itemData);

        if (!name) return "-";

        if (type === "file") {
          return (
            <Flex justify="space-between" align="center">
              <Button
                type="link"
                onClick={() => setFileModal(itemData)}
                style={{ minWidth: 0, padding: 0 }}
              >
                <Typography.Text ellipsis={{ tooltip: name }}>
                  {name}
                </Typography.Text>
              </Button>
              <DownloadLink
                show_icon={true}
                href={`/api/file/download?uuid=${itemData?.uuid}`}
                original_name={itemData?.original_name}
              />
            </Flex>
          );
        }

        const link = getResultLink({ type, data: itemData, isAdmins });

        if (!link) {
          return (
            <Tooltip title={name}>
              <span>{name}</span>
            </Tooltip>
          );
        }

        return (
          <CustomLink to={link} color="#303030">
            <Tooltip title={name}>{name}</Tooltip>
          </CustomLink>
        );
      },
    },
    {
      title: "Путь",
      key: "path",
      ellipsis: true,
      width: 200,
      render: (_, record) => {
        const pathText = getResultPathText({
          type: record?.type,
          data: record?.data,
          isAdmins,
        });

        return (
          <Tooltip title={pathText}>
            <span style={{ color: "#bbb" }}>{pathText || "-"}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "Описание",
      key: "description",
      width: 220,
      render: (_, record) =>
        record?.type === "file" ? (
          <ExpandableText value={record?.data?.description || ""} line={2} />
        ) : (
          ""
        ),
    },
    {
      title: "Автор",
      key: "author",
      width: 220,
      render: (_, record) => {
        const author = record?.data?.author;

        if (!author) return "-";

        return (
          <EmployeeProfile
            avatarSize={24}
            tooltip
            {...author}
            uuid={author.uuid}
          />
        );
      },
    },
  ];

  return (
    <Table
      showHeader={false}
      dataSource={data}
      columns={columns}
      rowKey={(record) => `${record?.type}-${record?.data?.uuid}`}
      loading={loading}
      pagination={pagination}
      onChange={onChange}
      scroll={scroll}
      tableLayout="fixed"
      bordered
    />
  );
};

export default SearchResultsTable;
